/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '../../box';
import { FrameworkImage } from '../../image-renderer';
import { HorizontalDivider } from '../../divider/horizontal-divider';
import { Typography } from '../typography';
import { Button } from '../../button/button';
import { useBreakpoint } from '../../use-breakpoint';
import {
  NetworkIconProps,
  ArrowRightIcon,
  CurrencyIconProps,
  CurrencyIcon,
} from '../../icons';
import { formatDate } from '../../nft-details-panel/utils';
import { NFTState } from '../../nft-track-card/types';
import { Pressable } from '../../pressable';

export interface HeroNFTPropsV3 {
  url: string;
  artistName: string;
  nftName: string;
  nftDescription: string;
  buttonCta: string;
  dateAdded: Date;
  image: string;
  network?: NetworkIconProps['name'] | null;
  totalSupply?: string | null;
  artistHandle?: string | null;
  isSold?: boolean;
  multiCopy?: boolean;
  price?: string | null;
  currencySymbol?: CurrencyIconProps['name'] | null;
  state?: NFTState;
  onPress: () => void;
}

export const HeroNFTV3: React.FC<HeroNFTPropsV3> = ({
  url,
  artistName,
  nftName,
  nftDescription,
  image,
  buttonCta,
  dateAdded,
  network,
  totalSupply,
  artistHandle,
  isSold,
  multiCopy,
  price,
  currencySymbol,
  state,
  onPress,
}: HeroNFTPropsV3) => {
  const { currentBreakpoint } = useBreakpoint();
  const [uiBreakPoint, setUiBreakPoint] = useState<
    '768px' | '960px' | '1200px' | '1440px' | null
  >(null);
  const isMobile1 = global?.window?.innerWidth <= 720 || false;
  const is768px = uiBreakPoint === '768px';
  const is1200px = uiBreakPoint && ['1440px', '1200px'].includes(uiBreakPoint);
  const is960px = uiBreakPoint && uiBreakPoint === '960px';

  useEffect(() => {
    setTimeout(() => {
      setUiBreakPoint(currentBreakpoint);
    }, 1000);
  }, [currentBreakpoint]);

  const textContainerWidth = !is1200px ? '50%' : 'calc(100% - 620px)';
  const imageConatinerWidth = is1200px ? '570px' : '50%';
  const heightWidth = is1200px ? { w: 480, h: 480 } : { w: 346, h: 320 };

  const TableRow: React.FC<{
    children: React.ReactNode;
    hasBottomBorder?: boolean;
  }> = ({ children, hasBottomBorder = true }) => {
    return (
      <Box
        flexDirection="row"
        borderBottomColor="grey24"
        borderBottomWidth={hasBottomBorder ? 'one' : 'zero'}
        borderBottomStyle="solid"
        paddingRight={['three', 'four']}
        paddingY="three"
        paddingLeft="zero"
      >
        <>{children}</>
      </Box>
    );
  };

  const totalSupplyMessage = useMemo(() => {
    if (state === 'traxxStemz') return '3030 available';
    if (state === 'emilg') return '25 available';
    if (state === 'coming soon') return 'Coming Soon';
    if (multiCopy) return totalSupply ? `Edition of ${totalSupply}` : ``;
    if (!isSold) return '1 available';
    return '0 available';
  }, [state, multiCopy, totalSupply, isSold]);

  const tableContents = useMemo(() => {
    switch (state) {
      case 'coming soon':
        return (
          <>
            {nftDescription ? (
              <TableRow hasBottomBorder={false}>
                <Box flexWrap="wrap" width="100%">
                  <Typography
                    fontSize="s"
                    css={{
                      color: 'var(--studio-secondaryText)',
                      'line-height': '1.8em !important',
                      'font-size': artistName === 'Goodwood' ? '18px'
                        : '14px',
                    }}
                  >
                    {nftDescription}
                  </Typography>
                  {dateAdded && artistName && (
                    <Typography textStyle="s" color="textDefault">
                      Added on {formatDate(dateAdded)} by {artistName}
                    </Typography>
                  )}
                </Box>
              </TableRow>
            ) : null}
          </>
        );
      default:
        return (
          <>
            <TableRow>
              <Box flex={[4, 3]}>
                <Box>
                  <Typography
                    fontSize="s"
                    css={{
                      color: 'var(--studio-secondaryText)',
                    }}
                  >
                    Price
                  </Typography>
                  <Box mt="two">
                    <Typography
                      fontSize="s"
                      css={{
                        color: 'var(--studio-secondaryText)',
                      }}
                    >
                      {!Number(price) ? 'Free' : price} {currencySymbol}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </TableRow>
            {nftDescription ? (
              <TableRow>
                <Box flexWrap="wrap" width="100%">
                  <Typography
                    fontSize="s"
                    css={{
                      color: 'var(--studio-secondaryText)',
                      'font-size': artistName === 'Goodwood' ? '18px'
                        : '14px',
                    }}
                  >
                    {nftDescription}
                  </Typography>
                  <Box mt="two">
                    {dateAdded && artistName && (
                      <Typography
                        fontSize="s"
                        css={{
                          color: 'var(--studio-secondaryText)',
                        }}
                      >
                        Added on {formatDate(dateAdded)} by {artistName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableRow>
            ) : null}
          </>
        );
    }
  }, [
    artistHandle,
    currencySymbol,
    dateAdded,
    nftDescription,
    network,
    price,
    state,
    totalSupplyMessage,
  ]);

  if (!uiBreakPoint) return null;

  if (isMobile1) {
    return (
      <Pressable onPress={onPress}>
        <Box
          width="100%"
          borderTopColor="grey32"
          borderTopWidth="one"
          borderTopStyle="solid"
          padding="four"
          paddingTop="eight"
          marginBottom="eight"
          alignItems="center"
        >
          <Box
            width="100%"
            height="348px"
            marginTop="eight"
            style={{paddingLeft: '5px', marginLeft: '-18px'}}
          >
            <Box
              maxWidth="100%"
              height="362px"
              width="100%"
              borderColor="outlines"
              borderRadius="two"
              borderWidth="one"
              borderStyle="solid"
              position="absolute"
              top="0px"
              overflow="hidden"
              backgroundColor="surfaceDark"
            >
              <FrameworkImage
                src={image || ''}
                alt="artistimage"
                layout="fill"
                objectFit="cover"
              />
            </Box>
          </Box>
          <Box width="100%" marginTop="eight">
            <Box flex={1}>
              <Box
                marginTop={['md', 'lg']}
                marginBottom={['md', 'lg']}
                marginRight={['md', 'lg']}
              >
                <Typography color="textLow" textStyle="h2"
                  css={{
                    fontSize: artistName === 'Goodwood' ? '18px' : '14px', // Apply font size conditionally
                  }}
                >
                  {artistName}
                </Typography>
                <Typography color="textDefault" textStyle="heroSmall">
                  {nftName}
                </Typography>
                {currencySymbol && (
                  <Box right="0px" bottom="10px" position="absolute">
                    <CurrencyIcon
                      name={currencySymbol}
                      size={18}
                      color="greyPrimary"
                    />
                  </Box>
                )}
              </Box>
              <HorizontalDivider color="grey24" />
              {tableContents}
            </Box>
            <Box mt="fifteen">
              <Button
                onPress={onPress}
                type="primary"
                leftAccessory={
                  <ArrowRightIcon
                    color={'var(--studio-primaryButtonText)' as any}
                  />
                }
                buttonStyle={{
                  backgroundColor: 'var(--studio-primaryButton)',
                  textColor: 'var(--studio-primaryButtonText)',
                  margin: '8px 6px 8px 6px',
                  width: '320px',
                  borderRadius: artistName === 'Goodwood' ? '0px' : '8px',
                  border: artistName === 'Goodwood' ? '1px solid black' : 'none'
                }}
                hasButtonStyle
              >
                {buttonCta || 'See more'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Pressable>
    );
  }

  if (is768px) {
    return (
      <Pressable onPress={onPress}>
        <Box
          flexDirection="row"
          width="100%"
          maxWidth="2000px"
          minHeight={heightWidth.h}
          borderTopColor="grey32"
          borderTopWidth="one"
          borderTopStyle="solid"
          paddingTop="eight"
          paddingBottom="eight"
          paddingLeft="three"
          paddingRight="three"
        >
          <Box
            width={imageConatinerWidth}
            style={!is1200px ? {} : {paddingLeft: '30px'}}
          >
            <Box
              maxWidth="100%"
              height={heightWidth.h}
              width={['100%', heightWidth.w]}
              borderColor="outlines"
              borderRadius="two"
              borderWidth="one"
              borderStyle="solid"
              position="absolute"
              top="0px"
              overflow="hidden"
              backgroundColor="surfaceDark"
            >
              <FrameworkImage
                src={image || ''}
                alt="artistimage"
                layout="fill"
                objectFit="cover"
              />
            </Box>
          </Box>
          <Box
            style={{
              width: textContainerWidth,
              paddingLeft: '30px',
            }}
          >
            <Box flex={1}>
              <Box
                marginTop={['md', 'lg']}
                marginBottom={['md', 'lg']}
                marginRight={['md', 'lg']}
              >
                <Typography
                  css={{
                    color: 'var(--studio-primaryText)',
                    'line-height': '1.8em',
                    fontSize: artistName === 'Goodwood' ? '18px' : '14px', 
                  }}
                  fontSize="xm"
                >
                  {artistName}
                </Typography>
                <Typography
                  css={{
                    color: 'var(--studio-primaryText)',
                    'line-height': '1.8em',
                  }}
                  fontSize="xm"
                >
                  {nftName}
                </Typography>
                {currencySymbol && (
                  <Box right="0px" bottom="10px" position="absolute">
                    <CurrencyIcon
                      name={currencySymbol}
                      size={18}
                      color="greyPrimary"
                    />
                  </Box>
                )}
              </Box>
              <HorizontalDivider color="grey24" />
              {tableContents}
            </Box>
            <Box mt="fifteen">
              <Button
                onPress={onPress}
                type="primary"
                leftAccessory={
                  <ArrowRightIcon
                    color={'var(--studio-primaryButtonText)' as any}
                  />
                }
                buttonStyle={{
                  backgroundColor: 'var(--studio-primaryButton)',
                  textColor: 'var(--studio-primaryButtonText)',
                  margin: '8px 6px 8px 6px',
                  width: '320px',
                  borderRadius: artistName === 'Goodwood' ? '0px' : '8px',
                  border: artistName === 'Goodwood' ? '1px solid black' : 'none'
                }}
                hasButtonStyle
              >
                {buttonCta || 'See more'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={onPress}>
      <Box
        flexDirection="row"
        width="100%"
        maxWidth="2000px"
        minHeight={heightWidth.h}
        borderTopColor="grey32"
        borderTopWidth="one"
        borderTopStyle="solid"
        paddingTop="eight"
        paddingBottom="eight"
        paddingLeft="three"
        paddingRight="three"
      >
        <Box
          width={imageConatinerWidth}
          style={!is1200px ? {} : {paddingLeft: '30px'}}
        >
          <Box
            maxWidth="100%"
            height={heightWidth.h}
            width={['100%', heightWidth.w]}
            borderColor="outlines"
            borderRadius="two"
            borderWidth="one"
            borderStyle="solid"
            position="absolute"
            top="0px"
            overflow="hidden"
            backgroundColor="surfaceDark"
          >
            <FrameworkImage
              src={image || ''}
              alt="artistimage"
              layout="fill"
              objectFit="cover"
            />
          </Box>
        </Box>
        <Box
          style={{
            width: textContainerWidth,
          }}
        >
          <Box flex={1}>
            <Box
              marginTop={['md', 'lg']}
              marginBottom={['md', 'lg']}
              marginRight={['md', 'lg']}
            >
              <Typography
                css={{
                  color: 'var(--studio-primaryText)',
                  'line-height': '1.8em',
                  fontSize: artistName === 'Goodwood' ? '18px' : '14px', 
                }}
                fontSize="xm"
              >
                {artistName}
              </Typography>
              <Typography
                fontSize="h2"
                css={{
                  color: 'var(--studio-secondaryText)',
                  'line-height': '1.8em !important',
                }}
              >
                {nftName}
              </Typography>
              {currencySymbol && (
                <Box right="0px" bottom="10px" position="absolute">
                  <CurrencyIcon
                    name={currencySymbol}
                    size={18}
                    color="greyPrimary"
                  />
                </Box>
              )}
            </Box>
            <HorizontalDivider color="grey24" />
            {tableContents}
          </Box>
          <Box mt="fifteen">
            <Button
              onPress={onPress}
              type="primary"
              leftAccessory={
                <ArrowRightIcon
                  color={'var(--studio-primaryButtonText)' as any}
                />
              }
              buttonStyle={{
                backgroundColor: 'var(--studio-primaryButton)',
                textColor: 'var(--studio-primaryButtonText)',
                margin: '8px 6px 8px 6px',
                width: '320px',
                borderRadius: artistName === 'Goodwood' ? '0px' : '8px',
                border: artistName === 'Goodwood' ? '1px solid black' : 'none'
              }}
              hasButtonStyle
            >
              {buttonCta || 'See more'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Pressable>
  );
};
